import React, { useState, useRef, useEffect } from "react";

/**
 * DatePicker Component
 * 
 * Props:
 * - value: string (ISO format "YYYY-MM-DD" or null)
 * - onChange: (dateString: string | null) => void (callback when date is set or cleared)
 */
export function DatePicker({ value, onChange, dropdownAlign = "left" }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Helper to format local date string with offset
  const getLocalDateString = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = getLocalDateString(0);
  const tomorrowStr = getLocalDateString(1);
  const nextWeekStr = getLocalDateString(7);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format pill display name and color
  let pillText = "Date";
  let pillClass = "text-gray-500 hover:bg-gray-100 border-gray-300";
  let iconColor = "text-gray-400";

  if (value) {
    if (value === todayStr) {
      pillText = "Today";
      pillClass = "text-green-600 bg-green-50 border-green-200 hover:bg-green-100";
      iconColor = "text-green-500";
    } else if (value === tomorrowStr) {
      pillText = "Tomorrow";
      pillClass = "text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100";
      iconColor = "text-orange-500";
    } else if (value === nextWeekStr) {
      pillText = "Next week";
      pillClass = "text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100";
      iconColor = "text-blue-500";
    } else {
      // Format as YYYY-MM-DD
      pillText = value;
      pillClass = "text-purple-600 bg-purple-50 border-purple-200 hover:bg-purple-100";
      iconColor = "text-purple-500";
    }
  }

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      {/* Pill Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-200 ${pillClass}`}
      >
        <span className={iconColor}>📅</span>
        <span>{pillText}</span>
        {value && (
          <span
            onClick={handleClear}
            className="ml-1 text-xs hover:bg-black/10 rounded-full w-4 h-4 flex items-center justify-center font-bold text-gray-500 transition-colors"
            title="Clear date"
          >
            ×
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 ${dropdownAlign === "right" ? "right-0" : "left-0"} w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 animate-fadeIn`}>
          {/* Quick Select Options */}
          <button
            type="button"
            onClick={() => {
              onChange(todayStr);
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="text-green-500">📅</span>
            <span>Today</span>
          </button>
          
          <button
            type="button"
            onClick={() => {
              onChange(tomorrowStr);
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="text-orange-500">📅</span>
            <span>Tomorrow</span>
          </button>

          <button
            type="button"
            onClick={() => {
              onChange(nextWeekStr);
              setIsOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <span className="text-blue-500">📅</span>
            <span>Next week</span>
          </button>

          <div className="border-t border-gray-100 my-1"></div>

          {/* Custom Date Input */}
          <div className="px-3 py-1.5">
            <span className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Custom Date</span>
            <input
              type="date"
              value={value || ""}
              onChange={(e) => {
                onChange(e.target.value || null);
                setIsOpen(false);
              }}
              className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
            />
          </div>
        </div>
      )}
    </div>
  );
}
